import { handleRedirectionWithSignupSource, staticFile } from '@common/utils'
import { FastlyImage } from '@components/common/image'
import Router from 'next/router'
import { useState } from 'react'

const SKILL_TYPES = {
  CODING_CHAMPION: 'Coding Champion',
  CODING_PRODIGY: 'Coding Prodigy',
  CODING_GRADMASTER: 'Coding Grandmaster',
}

type ModalProps = {
  title: string
  description: string
  content: string
  containerStyle: string
}

const Modal = ({
  title,
  description,
  content,
  containerStyle,
}: ModalProps): JSX.Element => {
  const backgroundColor =
    title === SKILL_TYPES.CODING_CHAMPION
      ? '#FFF5F7'
      : title === SKILL_TYPES.CODING_PRODIGY
      ? '#F7F2FE'
      : '#E9F2FF'

  const color =
    title === SKILL_TYPES.CODING_CHAMPION
      ? '#EB4962'
      : title === SKILL_TYPES.CODING_PRODIGY
      ? '#7E36D9'
      : '#7E36D9'
  return (
    <div
      className={`absolute mt-2 w-96 px-4 py-3 text-left hidden lg:flex lg:flex-col ${containerStyle}`}
      style={{
        border: `0.5px solid ${color}`,
        backgroundColor: backgroundColor,
        filter: 'drop-shadow(0px 4px 26px rgba(0, 0, 0, 0.1))',
        zIndex: 20,
      }}
    >
      <h4 className="font-600 text-2xl py-3" style={{ color }}>
        {title}
      </h4>
      <p className="text-xl py-3 text-grey">{description}</p>
      <p
        className="text-grey text-lg"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </div>
  )
}

const Skills = (): JSX.Element => {
  const [activeModal, setActiveModal] = useState(null)

  return (
    <div className="relative overflow-hidden">
      <div className="mx-5 mt-16">
        <h3 className="text-grey font-700 text-2xl lg:text-5xl text-center leading-tight mx-auto max-w-5xl">
          Our focus is on enriching core competencies in kids throughout their
          coding journey
        </h3>
        {/* display modal on larger devices */}
        <div className="flex-col justify-center mt-6 hidden lg:flex-row lg:flex shadow-lg container mx-auto items-center">
          <div className="flex flex-col order-1 my-5 lg:order-0 lg:m-20 flex-1">
            <div
              className="relative bg-white rounded-md px-8 py-4 text-center my-3 lg:my-10 lg:self-start"
              style={{
                border: '2px solid  #EB5757',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              }}
              onMouseEnter={() => setActiveModal(SKILL_TYPES.CODING_CHAMPION)}
              onMouseLeave={() => setActiveModal(null)}
            >
              <p className="text-2xl font-600">{SKILL_TYPES.CODING_CHAMPION}</p>
              {activeModal === SKILL_TYPES.CODING_CHAMPION && (
                <Modal
                  title="Coding Champion"
                  description="Get introduced to the fundamentals of coding and progress to complex concepts in 50 sessions."
                  content="With the topics covered in the Coding Champion classes for all grades 1-8, children will increase their logical and critical thinking. They will also progress to slightly more complex algorithms and design thinking skills.<br /><br />Through the activities and animation, game and app development, children will also develop creativity and learn real world application of coding skills by building interesting animations and games and apps."
                  containerStyle="lg:-top-10 lg:left-10 lg:ml-64"
                />
              )}
            </div>
            <div
              className="relative bg-white rounded-md px-8 py-4 text-center my-3 lg:my-10 lg:self-center"
              style={{
                border: '2px solid #7E36D9',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              }}
              onMouseEnter={() => setActiveModal(SKILL_TYPES.CODING_PRODIGY)}
              onMouseLeave={() => setActiveModal(null)}
            >
              <p className="text-2xl font-600">{SKILL_TYPES.CODING_PRODIGY}</p>
              {activeModal === SKILL_TYPES.CODING_PRODIGY && (
                <Modal
                  title="Coding Prodigy"
                  description="Get introduced to the fundamentals of coding and progress to advanced concepts in 98 sessions."
                  content="With the topics covered in the Coding Prodigy classes for all grades 1-8, children will increase not only their logical and critical thinking but also develop analytical thinking. They will progress to advanced algorithms and design thinking skills.<br /><br />Through the activities and animation, game and app development, children will also develop creativity and learn real world application of coding skills by building interesting animations and games and apps."
                  containerStyle="lg:ml-64 lg:-top-20"
                />
              )}
            </div>
            <div
              className="relative bg-white rounded-md px-8 py-4 text-center my-3 lg:my-10 lg:self-end -mr-8"
              style={{
                border: '2px solid #2F80ED',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              }}
              onMouseEnter={() => setActiveModal(SKILL_TYPES.CODING_GRADMASTER)}
              onMouseLeave={() => setActiveModal(null)}
            >
              <p className="text-2xl font-600">
                {SKILL_TYPES.CODING_GRADMASTER}
              </p>
              {activeModal === SKILL_TYPES.CODING_GRADMASTER && (
                <Modal
                  title="Coding Grandmaster"
                  description="A course for those who want to master the art of coding and create a better future through code."
                  content="Key learnings include all Coding Prodigy modules along with advanced Scratch game development, Android game development, website development, artificial intelligence, machine learning, speech and image recognition, sensors, Python, Java and more.<br /><br />The course is designed to improve analytical skills, critical thinking, problem solving skills, and improve conceptual clarity on complex concepts."
                  containerStyle="lg:ml-64 lg:-top-20"
                />
              )}
            </div>
          </div>
          <div
            className={`${
              activeModal ? 'lg:opacity-50' : 'lg:opacity-100'
            }order-0 my-5 lg:order-1 lg:w-1/2 lg:m-161`}
          >
            <FastlyImage
              src="/images/curriculum/skills/skills-chart.svg"
              alt="skills chart"
              className={`lg:h-128 ${
                activeModal ? 'lg:opacity-25' : 'lg:opacity-100'
              }`}
              height={512}
              width={512}
            />
          </div>
        </div>
        {/* display legend on smaller devices */}
        <div className="flex flex-col my-10  lg:hidden">
          <FastlyImage
            src={staticFile('/images/curriculum/skills/skills-chart.svg')}
            alt="skills chart"
            className="lg:h-128"
            height={538}
            width={682}
          />
          <div className="flex md:flex-wrap flex-col md:flex-row mx-auto justify-center mt-10 sm:space-x-2 lg:space-x-0 lg:hidden lg:mx-10 lg:mt-0">
            <div className="flex my-2">
              <div
                className="h-6 w-6 mx-2"
                style={{
                  backgroundColor: '#FF5F5F',
                  border: '1px solid #EB4962',
                }}
              />
              <p className="text-grey-900">Coding Champion</p>
            </div>
            <div className="flex my-2">
              <div
                className="h-6 w-6 mx-2"
                style={{
                  backgroundColor: '#C59DFD',
                  border: '1px solid #7E36D9',
                }}
              />
              <p className="text-grey-900">Coding Prodigy</p>
            </div>
            <div className="flex my-2">
              <div
                className="h-6 w-6 mx-2"
                style={{
                  backgroundColor: '#45ABE6',
                  border: '1px solid #2F80ED',
                }}
              />
              <p className="text-grey-900">Coding Grandmaster</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center pt-6">
        <button
          onClick={() =>
            handleRedirectionWithSignupSource(Router.asPath, '/register')
          }
          className={`btn btn-orange py-3 text-xl font-600 my-5 ${
            activeModal ? 'lg:opacity-25' : 'lg:opacity-100'
          }`}
        >
          Try a free lesson
        </button>
      </div>
    </div>
  )
}

export default Skills
